// src/ecrans/orchestrateur/avis-calculs.js — « La propreté, vue par les
// voyageurs » (pur, testé). Échelle unique du journal de la maquette :
// Airbnb 5 excellent · 4,8 à moins de 5 correct · sous 4,8 à améliorer ;
// Booking 10 excellent · 8 à moins de 10 correct · sous 8 à améliorer.

/** Niveau d'une note : ok (vert), gr (gris), ab (aubergine) ; null sans note. */
export function niveau(note, sur) {
  if (note === null || note === undefined) return null;
  if (sur === 10) return note >= 10 ? "ok" : note >= 8 ? "gr" : "ab";
  return note >= 5 ? "ok" : note >= 4.8 ? "gr" : "ab";
}

/** « 4,5 » ; « — » sans note, jamais 0. */
export function noteLue(note) {
  return note === null || note === undefined ? "—" : String(note).replace(".", ",");
}

/** Le nom d'un mois : clé « 2026-07 ». */
export const moisDe = (a) => String(a.date || "").slice(0, 7);

/** Les mois présents, du plus récent au plus ancien. */
export function moisPresents(avis) {
  return [...new Set((avis || []).map(moisDe).filter(Boolean))].sort().reverse();
}

export function filtrer(avis, { mois, plateforme, logement }) {
  return (avis || []).filter(
    (a) =>
      (mois === "tout" || moisDe(a) === mois) &&
      (plateforme === "tous" || a.plateforme === plateforme) &&
      (logement === "tous" || a.logement.id === logement),
  );
}

function moyenne(notes) {
  const v = notes.filter((n) => n !== null && n !== undefined);
  if (!v.length) return null;
  return Math.round((v.reduce((x, y) => x + y, 0) / v.length) * 10) / 10;
}

/** La bande des logements : propreté Airbnb et Booking, les moins bons d'abord. */
export function bandeLogements(logements, avis) {
  return (logements || [])
    .map((l) => {
      const siens = (avis || []).filter((a) => a.logement.id === l.id);
      const airbnb = moyenne(siens.filter((a) => a.sur === 5).map((a) => a.note_proprete));
      const booking = moyenne(siens.filter((a) => a.sur === 10).map((a) => a.note_proprete));
      return {
        ...l,
        n: siens.length,
        airbnb,
        booking,
        aRegarder: siens.filter((a) => niveau(a.note_proprete, a.sur) === "ab").length,
        pire: Math.min(airbnb === null ? 9 : airbnb / 5, booking === null ? 9 : booking / 10),
      };
    })
    .filter((l) => l.n > 0)
    .sort((a, b) => a.pire - b.pire);
}

/**
 * Les groupes : propreté sous le seuil ; le voyageur parle du ménage (tri de
 * l'opérateur, `parle_du_menage === true`) ; les autres. Jamais de liste de
 * mots ici : le journal de la maquette l'interdit.
 */
export function groupesAvis(avis) {
  const sous = [];
  const parle = [];
  const autres = [];
  for (const a of avis || []) {
    if (niveau(a.note_proprete, a.sur) === "ab") sous.push(a);
    else if (a.parle_du_menage === true) parle.push(a);
    else autres.push(a);
  }
  return { sous, parle, autres };
}

/** « contrôlé, 2 reprises (sol, miroir) » */
export function phraseControle(c) {
  if (!c) return "—";
  if (c.etat === "sans_controle") return "sans contrôle";
  if (c.etat === "rien_a_reprendre") return "contrôlé, rien à reprendre";
  const zones = c.zones && c.zones.length ? ` (${c.zones.join(", ")})` : "";
  return `contrôlé, ${c.reprises} reprise${c.reprises > 1 ? "s" : ""}${zones}`;
}

export const PAR_AGENT_MIN = 10;

/** « Par agent » : un indice par prénom, jamais d'agent inconnu inventé. */
export function parAgent(avis) {
  const groupes = new Map();
  for (const a of avis || []) {
    const prenom = a.menage && a.menage.agent ? a.menage.agent.prenom : null;
    if (!prenom) continue;
    const g = groupes.get(prenom) || { prenom, n: 0, sous: 0, reprises: 0 };
    g.n += 1;
    if (niveau(a.note_proprete, a.sur) === "ab") g.sous += 1;
    g.reprises += a.menage.controle.reprises || 0;
    groupes.set(prenom, g);
  }
  return [...groupes.values()].sort((x, y) => x.prenom.localeCompare(y.prenom, "fr"));
}
